import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IApplicationState } from '../../store';
import { actionCreators as BookingActions, BookingStatus } from '../../store/Booking';
import { actionCreators as PlayerActions } from '../../store/Players';
import { actionCreators as RewardActions } from '../../store/Rewards';
import { actionCreators as PaymentOptionActions } from '../../store/PaymentOption';
import { PlayerCountUpsellFlow } from '../../helpers/PlayerCountUpsellFlow';

const useUpsellAccept = (upsellFlow: PlayerCountUpsellFlow): (newPlayerCount: number) => boolean => {
	const dispatch = useDispatch();
	const players = useSelector((state: IApplicationState) => state.players.selectedPlayers);
	const pendingBooking = useSelector((state: IApplicationState) => state.booking.booking);
	const bookingStatus = useSelector((state: IApplicationState) => state.booking.status);
	const selectedPaymentOption = useSelector((state: IApplicationState) => state.paymentOption.selectedOption);

	return useCallback(
		(newPlayerCount: number): boolean => {
			const offerMet = upsellFlow.isOfferMet(newPlayerCount);
			const previousCount = players ?? 0;
			const hasPendingBooking = pendingBooking && bookingStatus === BookingStatus.Pending;

			if (hasPendingBooking) {
				// AFTER RESERVATION: update the server-side booking first.
				// Player count is deferred until the API confirms, so the widget
				// stays in its current state until the server accepts the change.
				dispatch(BookingActions.updateBookingSlots(newPlayerCount));
			} else {
				// BEFORE RESERVATION: no booking exists yet, so update the player
				// count immediately. The count will be used when reserveBooking is
				// called later at checkout.
				dispatch(PlayerActions.selectPlayers(newPlayerCount));
			}

			dispatch(RewardActions.upsellPlayersUpdated(previousCount, newPlayerCount, upsellFlow.minimumTicketCount, offerMet));
			if (offerMet) {
				dispatch(RewardActions.upsellOfferAccepted(previousCount, newPlayerCount, upsellFlow.minimumTicketCount));
				if (selectedPaymentOption === 'split') {
					dispatch(PaymentOptionActions.resetPaymentOption());
				}
				if (selectedPaymentOption === 'deposit') {
					dispatch(PaymentOptionActions.resetPaymentOption());
				}
			}
			return hasPendingBooking ? false : offerMet;
		},
		[dispatch, pendingBooking, bookingStatus, players, upsellFlow, selectedPaymentOption],
	);
};

export default useUpsellAccept;
